import { useMemo } from 'react';
import { Button, Typography, makeStyles } from '@material-ui/core';
import { t } from '@lingui/macro';

import { agentFilterId, dateTimeFilterId, useTest } from '../filters';
import { dateTimeFormatter } from '../../utilities';
import StatusBlock from '../status-block/StatusBlock';
import { problemSeverity } from '../data-table/columns/Status';
import { PathVisualizationChart } from './PathVisualizationChart';
import { PathVisualizationInfo } from './PathVisualizationInfo';
import Link, { buildLinkUrl } from '../links/Link';
import { useFilterState } from '../../stores/FilterStateStore';
import { usePageState } from '../../stores/PageStateStore';
import { usePathChartTableViewports } from './ChartTableViewportsContext';
import useIsPrinting from '../../utilities/PrintUtilities';

const useStyles = makeStyles(() => ({
    columnWrapper: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        padding: '16px 0',
    },
    columnHeader: {
        padding: '0 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',

        '& a': {
            lineHeight: '20px',
            padding: '4px 8px',
        },
    },
    timeStatusText: {
        display: 'flex',
        gap: '24px',
    },
    pathInfoText: {
        padding: '8px 16px',
        width: 'fit-content',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    pathDataUnavailableWrapper: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: ' center',
        justifyContent: 'center',
    },
}));

export function PathVisualizationColumnRenderer(row) {
    const viewports = usePathChartTableViewports();
    const styles = useStyles();
    const isPrinting = useIsPrinting();
    const test = useTest();
    const [filterState] = useFilterState();
    const [pageState] = usePageState();
    const link = useMemo(
        () => ({
            target: '/path-detail',
            mappedFilterParams: {
                [agentFilterId]: 'agent',
                [dateTimeFilterId]: 'testTime',
            },
        }),
        []
    );
    const targetUrl = useMemo(
        () => buildLinkUrl(link, row, filterState, pageState),
        [link, row, filterState, pageState]
    );

    const viewportKey = `${row.agent.uuid}-${row.testTime}`;
    const viewport = viewports.get(viewportKey);

    return (
        <div className={styles.columnWrapper}>
            <div className={styles.columnHeader}>
                <div className={styles.timeStatusText}>
                    <StatusBlock color={problemSeverity(row.status.worst)} />
                    <Typography variant="body2">{dateTimeFormatter(row.testTime, { type: 'dateTime' })}</Typography>
                </div>
                {!isPrinting && (
                    <Button size="small">
                        <Link
                            className={styles.link}
                            to={targetUrl}
                            state={{ fromTestLogPage: true }}
                            plmIdDefault={row?.plmId}
                        >
                            Path Details
                        </Link>
                    </Button>
                )}
            </div>
            {row.path.length > 1 ? (
                <>
                    <PathVisualizationInfo
                        pathDelay={row.pathLatency?.avg}
                        pathNumberOfHops={row.pathHops?.max}
                        statusMessage={row.path?.at(-1).statusMessage}
                    />
                    <PathVisualizationChart data={row} test={test} viewport={viewport} viewportKey={viewportKey} />
                </>
            ) : (
                <div className={styles.pathDataUnavailableWrapper}>
                    <Typography>{t`Path data unavailable for this test run.`}</Typography>
                </div>
            )}
        </div>
    );
}
